import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CURRENCY_CATALOG, currencyName } from "@/lib/currencies";
import { Send, Save, Trash2, ArrowDownLeft, ArrowUpRight, Wallet, Landmark, FileText, Search, ScanLine, HandCoins, CheckCircle2, MessageCircle, MessageSquare, Settings } from "lucide-react";

const CUR = CURRENCY_CATALOG.map((c) => c.code);
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_WA_SENDER = "مرحباً {sender_name}،\nتم استلام حوالتك رقم {code} بقيمة {amount} {currency}.\nالمستفيد: {receiver_name}\nالحالة: {status}\nرابط الإيصال: {receipt_link}\n{company_name}\n{office_address}";
const DEFAULT_WA_RECEIVER = "مرحباً {receiver_name}،\nلديك حوالة رقم {code} بقيمة {payout_amount} {payout_currency} جاهزة للاستلام.\nالمرسل: {sender_name}\nرابط الإيصال: {receipt_link}\nيرجى مراجعتنا برمز الحوالة {code}.\n{company_name}\n{office_address}";
const SAMPLE_REM = { sender_name: "أحمد الخالد", receiver_name: "ليلى حسن", code: "HW00001", amount: 1000, currency: "USD", payout_amount: 985, payout_currency: "USD", commission: 15, status: "قيد التنفيذ", destination: "دمشق", agent: "مكتب إسطنبول", receipt_link: `${BACKEND}/api/public/remittance-receipt/XXXX` };
// Manual WhatsApp: open wa.me with a pre-filled message the operator sends from their own WhatsApp.
const waSend = (phone, text) => {
  const digits = String(phone || "").replace(/[^\d]/g, "");
  if (!digits) { toast.error("لا يوجد رقم هاتف لهذا الطرف"); return; }
  window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, "_blank");
};
const STATUS = { pending: "قيد التنفيذ", completed: "مكتملة", paid: "مدفوعة للمستفيد", cancelled: "ملغاة" };
const STATUS_CLR = { pending: "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700", paid: "bg-blue-100 text-blue-700", cancelled: "bg-rose-100 text-rose-700" };

const empty = (base) => ({
  direction: "out", sender_name: "", sender_phone: "", sender_id: "",
  receiver_name: "", receiver_phone: "", amount: "", currency: base,
  payout_currency: base, payout_amount: "", commission: "",
  destination: "", agent: "", code: "", notes: "",
  settlement: "cash", bank_account_id: "",
});

export default function Remittances() {
  const { user, currentCompany } = useAuth();
  const base = (currentCompany?.base_currency || "SYP").toUpperCase();
  const canDelete = ["super_admin", "company_manager"].includes(user?.role);
  const canManage = ["super_admin", "company_manager"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty(base));
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scan, setScan] = useState("");
  const [act, setAct] = useState({ open: false, type: "payout", rem: null, settlement: "cash", bank_account_id: "" });
  const [settings, setSettings] = useState({ office_address: "", office_whatsapp: "", logo: "", wa_sender_template: DEFAULT_WA_SENDER, wa_receiver_template: DEFAULT_WA_RECEIVER });
  const [setDlg, setSetDlg] = useState(false);
  const [sForm, setSForm] = useState({ office_address: "", office_whatsapp: "", logo: "", wa_sender_template: DEFAULT_WA_SENDER, wa_receiver_template: DEFAULT_WA_RECEIVER });
  const [savingSet, setSavingSet] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const [r, b, s, cfg] = await Promise.all([
        api.get("/remittances", { params }),
        api.get("/bank-accounts").catch(() => ({ data: [] })),
        api.get("/exchange/summary").catch(() => ({ data: null })),
        api.get("/exchange/settings").catch(() => ({ data: null })),
      ]);
      setItems(r.data || []); setBanks(b.data || []); setSummary(s.data || null);
      if (cfg.data) setSettings(cfg.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { setForm(empty(base)); /* eslint-disable-next-line */ }, [currentCompany?.id]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentCompany?.id, q, statusFilter]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isOut = form.direction === "out";

  const submit = async () => {
    if (!(+form.amount > 0)) { toast.error("أدخل مبلغ الحوالة"); return; }
    if (isOut && !form.sender_name.trim()) { toast.error("اسم المرسل مطلوب"); return; }
    if (!form.receiver_name.trim()) { toast.error("اسم المستلم مطلوب"); return; }
    if (form.settlement === "bank" && !form.bank_account_id) { toast.error("اختر الحساب البنكي"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, amount: +form.amount, commission: +form.commission || 0,
        payout_currency: form.payout_currency || form.currency,
        payout_amount: form.payout_amount === "" ? null : +form.payout_amount,
      };
      const { data } = await api.post("/remittances", payload);
      toast.success(`تم إنشاء الحوالة ${data.code}`);
      setForm(empty(base)); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف الحوالة وعكس كل أثرها المالي؟")) return;
    try { await api.delete(`/remittances/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const cancelRem = async (id) => {
    try { await api.put(`/remittances/${id}/status`, null, { params: { status: "cancelled" } }); toast.success("تم الإلغاء"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const openReceipt = async (id) => {
    try {
      const res = await api.get(`/remittances/${id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const startAction = (type, rem) => setAct({ open: true, type, rem, settlement: "cash", bank_account_id: "" });
  const doAction = async () => {
    const { type, rem, settlement, bank_account_id } = act;
    if (settlement === "bank" && !bank_account_id) { toast.error("اختر الحساب البنكي"); return; }
    try {
      await api.post(`/remittances/${rem.id}/${type}`, { settlement, bank_account_id: bank_account_id || null });
      toast.success(type === "payout" ? "تم الصرف للمستفيد" : "تمت التسوية مع الوكيل");
      setAct({ open: false, type: "payout", rem: null, settlement: "cash", bank_account_id: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const lookup = async () => {
    if (!scan.trim()) return;
    try {
      const { data } = await api.get("/remittances/lookup", { params: { code: scan.trim() } });
      setQ(scan.trim()); setStatusFilter("all");
      toast.success(`الحوالة ${data.code} — ${STATUS[data.status] || data.status}`);
      setScan("");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const fillTemplate = (tpl, r) => {
    const map = {
      sender_name: r.sender_name || "", receiver_name: r.receiver_name || "",
      code: r.code || "", amount: r.amount ?? "", currency: r.currency || "",
      payout_amount: r.payout_amount ?? "", payout_currency: r.payout_currency || "",
      commission: r.commission ?? "", status: STATUS[r.status] || r.status || "",
      destination: r.destination || "", agent: r.agent || "",
      company_name: currentCompany?.name || "", office_address: settings?.office_address || "",
      receipt_link: r.receipt_link || (r.public_token ? `${BACKEND}/api/public/remittance-receipt/${r.public_token}` : ""),
    };
    let out = tpl || "";
    Object.entries(map).forEach(([k, v]) => { out = out.split(`{${k}}`).join(String(v)); });
    return out.replace(/\n{2,}/g, "\n").trim();
  };
  const previewMsg = (tpl) => {
    const map = { ...SAMPLE_REM, status: SAMPLE_REM.status, company_name: currentCompany?.name || "شركتك", office_address: sForm.office_address || "" };
    let out = tpl || "";
    Object.entries(map).forEach(([k, v]) => { out = out.split(`{${k}}`).join(String(v)); });
    return out.replace(/\n{2,}/g, "\n").trim();
  };
  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 600 * 1024) { toast.error("حجم الشعار كبير — اختر صورة أصغر من 600KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setSForm((s) => ({ ...s, logo: reader.result }));
    reader.readAsDataURL(f);
  };
  const waSender = (r) => waSend(r.sender_phone, fillTemplate(settings.wa_sender_template || DEFAULT_WA_SENDER, r));
  const waReceiver = (r) => waSend(r.receiver_phone, fillTemplate(settings.wa_receiver_template || DEFAULT_WA_RECEIVER, r));

  const openSettings = () => { setSForm({ office_address: settings.office_address || "", office_whatsapp: settings.office_whatsapp || "", logo: settings.logo || "", wa_sender_template: settings.wa_sender_template || DEFAULT_WA_SENDER, wa_receiver_template: settings.wa_receiver_template || DEFAULT_WA_RECEIVER }); setSetDlg(true); };
  const saveSettings = async () => {
    setSavingSet(true);
    try {
      const { data } = await api.put("/exchange/settings", sForm);
      setSettings(data); setSetDlg(false); toast.success("تم حفظ قوالب واتساب");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSavingSet(false); }
  };

  const sumRow = (obj) => Object.entries(obj || {}).map(([c, v]) => `${(+v).toLocaleString("ar-EG")} ${c}`).join(" · ") || "—";

  return (
    <div className="space-y-6" dir="rtl" data-testid="remittances-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <Send className="w-7 h-7 text-primary" /> الحوالات المالية
          </h1>
          <p className="text-sm text-muted-foreground mt-1">حوالات صادرة/واردة ببيانات المرسل والمستلم — تنعكس على الصندوق أو البنك مع تسوية الوكيل والمستفيد.</p>
        </div>
        {canManage && (
          <Button variant="outline" className="gap-2 h-10" onClick={openSettings} data-testid="remittance-settings-btn">
            <Settings className="w-4 h-4" /> قوالب واتساب
          </Button>
        )}
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="remittance-summary">
          <Card className="p-4">
            <div className="text-xs font-semibold text-muted-foreground mb-1">مستحق للمكاتب (صادرة)</div>
            <div className="text-sm font-bold tabular-nums" data-testid="summary-agent-payable">{sumRow(summary.agent_payable)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">مفتوحة: {summary.open_out}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-semibold text-muted-foreground mb-1">مستحق من المكاتب (واردة)</div>
            <div className="text-sm font-bold tabular-nums" data-testid="summary-agent-receivable">{sumRow(summary.agent_receivable)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">مفتوحة: {summary.open_in}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-semibold text-muted-foreground mb-1">مستحق للمستفيدين</div>
            <div className="text-sm font-bold tabular-nums" data-testid="summary-beneficiary-payable">{sumRow(summary.beneficiary_payable)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">بانتظار الصرف: {summary.unpaid_beneficiaries}</div>
          </Card>
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="text-xs font-semibold text-primary mb-1">مراكز العملات (صندوق+بنك)</div>
            <div className="text-xs font-bold tabular-nums space-y-0.5" data-testid="summary-positions">
              {(summary.positions || []).map((p) => <div key={p.currency}>{p.currency}: {p.total.toLocaleString("ar-EG")}</div>)}
            </div>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Create form */}
        <Card className="p-6 space-y-4" data-testid="remittance-form">
          <div className="grid grid-cols-2 gap-2" data-testid="remittance-direction">
            <button type="button" onClick={() => set("direction", "out")} className={`h-11 rounded-md border flex items-center justify-center gap-2 text-sm font-semibold transition-all ${isOut ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`} data-testid="remittance-dir-out">
              <ArrowUpRight className="w-4 h-4" /> حوالة صادرة
            </button>
            <button type="button" onClick={() => set("direction", "in")} className={`h-11 rounded-md border flex items-center justify-center gap-2 text-sm font-semibold transition-all ${!isOut ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`} data-testid="remittance-dir-in">
              <ArrowDownLeft className="w-4 h-4" /> حوالة واردة
            </button>
          </div>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">بيانات المرسل</div>
            <Input value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="اسم المرسل" className="h-10" data-testid="remittance-sender-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.sender_phone} onChange={(e) => set("sender_phone", e.target.value)} placeholder="هاتف المرسل" className="h-10" data-testid="remittance-sender-phone" />
              <Input value={form.sender_id} onChange={(e) => set("sender_id", e.target.value)} placeholder="رقم الهوية / الجواز" className="h-10" data-testid="remittance-sender-id" />
            </div>
          </div>

          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">بيانات المستلم</div>
            <Input value={form.receiver_name} onChange={(e) => set("receiver_name", e.target.value)} placeholder="اسم المستلم" className="h-10" data-testid="remittance-receiver-name" />
            <Input value={form.receiver_phone} onChange={(e) => set("receiver_phone", e.target.value)} placeholder="هاتف المستلم" className="h-10" data-testid="remittance-receiver-phone" />
          </div>

          <div className="grid grid-cols-[1fr,130px] gap-2">
            <div><Label>{isOut ? "المبلغ المستلم من المرسل" : "المبلغ الوارد من الوكيل"}</Label>
              <Input type="number" step="any" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="h-11 tabular-nums text-lg font-bold" data-testid="remittance-amount" /></div>
            <div><Label>العملة</Label>
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v, payout_currency: v }))}>
                <SelectTrigger className="h-11" data-testid="remittance-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{CUR.map((c) => <SelectItem key={c} value={c}>{currencyName(c)} ({c})</SelectItem>)}</SelectContent>
              </Select></div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label>العمولة</Label>
              <Input type="number" step="any" min="0" value={form.commission} onChange={(e) => set("commission", e.target.value)} className="h-11 tabular-nums" data-testid="remittance-commission" /></div>
            <div><Label>الرمز (اختياري)</Label>
              <Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="يُولّد تلقائياً" className="h-11" data-testid="remittance-code" /></div>
          </div>

          {/* Payout to beneficiary (currency may differ) */}
          <div className="rounded-md border border-dashed border-primary/30 p-3 space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">ما يستلمه المستفيد (اترك المبلغ فارغاً للحساب التلقائي)</div>
            <div className="grid grid-cols-[1fr,130px] gap-2">
              <Input type="number" step="any" min="0" value={form.payout_amount} onChange={(e) => set("payout_amount", e.target.value)} placeholder="تلقائي (المبلغ − العمولة)" className="h-10 tabular-nums" data-testid="remittance-payout-amount" />
              <Select value={form.payout_currency} onValueChange={(v) => set("payout_currency", v)}>
                <SelectTrigger className="h-10" data-testid="remittance-payout-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{CUR.map((c) => <SelectItem key={c} value={c}>{currencyName(c)} ({c})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label>الوجهة</Label>
              <Input value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="مدينة / دولة" className="h-10" data-testid="remittance-destination" /></div>
            <div><Label>الوكيل / المكتب</Label>
              <Input value={form.agent} onChange={(e) => set("agent", e.target.value)} placeholder="المكتب المراسل" className="h-10" data-testid="remittance-agent" /></div>
          </div>

          {isOut && (
            <div className="rounded-md border border-border p-3 space-y-2">
              <div className="text-xs font-semibold text-muted-foreground">تحصيل المبلغ من المرسل عبر</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { set("settlement", "cash"); set("bank_account_id", ""); }} className={`h-10 rounded-md border flex items-center justify-center gap-2 text-sm font-semibold transition-all ${form.settlement === "cash" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`} data-testid="remittance-settle-cash">
                  <Wallet className="w-4 h-4" /> الصندوق
                </button>
                <button type="button" onClick={() => set("settlement", "bank")} className={`h-10 rounded-md border flex items-center justify-center gap-2 text-sm font-semibold transition-all ${form.settlement === "bank" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`} data-testid="remittance-settle-bank">
                  <Landmark className="w-4 h-4" /> حساب بنكي
                </button>
              </div>
              {form.settlement === "bank" && (
                <Select value={form.bank_account_id || ""} onValueChange={(v) => set("bank_account_id", v)}>
                  <SelectTrigger className="h-10" data-testid="remittance-bank-account"><SelectValue placeholder="اختر الحساب البنكي" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.bank_name} · {b.currency}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          )}

          <div><Label>ملاحظات (اختياري)</Label>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="h-10" data-testid="remittance-notes" /></div>

          <Button onClick={submit} disabled={saving} className="w-full h-11" data-testid="remittance-submit">
            <Save className="w-4 h-4 me-2" /> {saving ? "جارٍ الحفظ..." : "إنشاء الحوالة"}
          </Button>
        </Card>

        {/* List + search */}
        <Card className="overflow-hidden" data-testid="remittance-list">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute top-2.5 start-2.5 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالرمز/الاسم/الهاتف" className="h-9 ps-8" data-testid="remittance-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-32" data-testid="remittance-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">كل الحالات</SelectItem>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <ScanLine className="w-4 h-4 absolute top-2.5 start-2.5 text-primary" />
                <Input value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder="امسح/أدخل رمز الحوالة ثم Enter" className="h-9 ps-8" data-testid="remittance-scan-input" />
              </div>
              <Button variant="outline" className="h-9" onClick={lookup} data-testid="remittance-scan-btn">بحث</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-start">الرمز</TableHead>
                  <TableHead className="text-start">النوع</TableHead>
                  <TableHead className="text-start">مرسل ← مستلم</TableHead>
                  <TableHead className="text-center">المبلغ</TableHead>
                  <TableHead className="text-center">الحالة</TableHead>
                  <TableHead className="text-center">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid={`remittance-row-${r.id}`}>
                    <TableCell className="text-start font-mono text-xs font-bold">{r.code}</TableCell>
                    <TableCell className="text-start">
                      {r.direction === "out"
                        ? <span className="text-xs inline-flex items-center gap-1 text-primary"><ArrowUpRight className="w-3.5 h-3.5" /> صادرة</span>
                        : <span className="text-xs inline-flex items-center gap-1 text-emerald-700"><ArrowDownLeft className="w-3.5 h-3.5" /> واردة</span>}
                    </TableCell>
                    <TableCell className="text-start text-xs">{r.sender_name || "—"} ← {r.receiver_name || "—"}{r.destination ? <div className="text-muted-foreground">{r.destination}</div> : null}</TableCell>
                    <TableCell className="text-center tabular-nums font-semibold">{fmtMoney(r.amount, r.currency)}{r.commission > 0 && <div className="text-[10px] text-muted-foreground">+عمولة {fmtMoney(r.commission, r.currency)}</div>}</TableCell>
                    <TableCell className="text-center">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_CLR[r.status] || ""}`} data-testid={`remittance-status-${r.id}`}>{STATUS[r.status] || r.status}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {r.direction === "in" && !r.beneficiary_paid && r.status !== "cancelled" && (
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-blue-700" onClick={() => startAction("payout", r)} data-testid={`remittance-payout-${r.id}`}><HandCoins className="w-3.5 h-3.5" /> صرف</Button>
                        )}
                        {!r.agent_settled && r.status !== "cancelled" && (
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-emerald-700" onClick={() => startAction("settle", r)} data-testid={`remittance-settle-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> تسوية</Button>
                        )}
                        {r.sender_phone && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-teal-600" title="إرسال للمرسل عبر واتساب" data-testid={`remittance-whatsapp-sender-${r.id}`} onClick={() => waSender(r)}>
                            <MessageSquare className="w-4 h-4" /></Button>
                        )}
                        {r.receiver_phone && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" title="إرسال للمستفيد عبر واتساب" data-testid={`remittance-whatsapp-receiver-${r.id}`} onClick={() => waReceiver(r)}>
                            <MessageCircle className="w-4 h-4" /></Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => openReceipt(r.id)} data-testid={`remittance-receipt-${r.id}`} title="إيصال PDF"><FileText className="w-4 h-4" /></Button>
                        {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r.id)} data-testid={`remittance-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد حوالات.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Payout / Settle dialog */}
      <Dialog open={act.open} onOpenChange={(o) => setAct((a) => ({ ...a, open: o }))}>
        <DialogContent dir="rtl" data-testid="remittance-action-dialog">
          <DialogHeader><DialogTitle>{act.type === "payout" ? "صرف الحوالة للمستفيد" : "تسوية الحوالة مع الوكيل/المكتب"}</DialogTitle></DialogHeader>
          {act.rem && (
            <div className="space-y-3">
              <div className="text-sm bg-muted/50 rounded-md p-3">
                <div><b>{act.rem.code}</b> — {act.rem.direction === "out" ? "صادرة" : "واردة"}</div>
                <div className="text-muted-foreground">
                  {act.type === "payout"
                    ? `يُصرف للمستفيد: ${act.rem.payout_amount} ${act.rem.payout_currency}`
                    : (act.rem.direction === "out" ? `يُدفع للوكيل: ${act.rem.amount} ${act.rem.currency}` : `يُحصّل من الوكيل: ${act.rem.amount} ${act.rem.currency}`)}
                </div>
              </div>
              <div>
                <Label className="text-xs">عبر</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setAct((a) => ({ ...a, settlement: "cash", bank_account_id: "" }))} className={`h-10 rounded-md border flex items-center justify-center gap-2 text-sm font-semibold ${act.settlement === "cash" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`} data-testid="action-settle-cash"><Wallet className="w-4 h-4" /> الصندوق</button>
                  <button type="button" onClick={() => setAct((a) => ({ ...a, settlement: "bank" }))} className={`h-10 rounded-md border flex items-center justify-center gap-2 text-sm font-semibold ${act.settlement === "bank" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`} data-testid="action-settle-bank"><Landmark className="w-4 h-4" /> حساب بنكي</button>
                </div>
              </div>
              {act.settlement === "bank" && (
                <Select value={act.bank_account_id || ""} onValueChange={(v) => setAct((a) => ({ ...a, bank_account_id: v }))}>
                  <SelectTrigger className="h-10" data-testid="action-bank-account"><SelectValue placeholder="اختر الحساب البنكي" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.bank_name} · {b.currency}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={doAction} data-testid="action-confirm">تأكيد</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp templates settings */}
      <Dialog open={setDlg} onOpenChange={setSetDlg}>
        <DialogContent dir="rtl" className="max-w-lg" data-testid="remittance-settings-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-primary" /> قوالب رسائل واتساب</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">عنوان المكتب / معلومات التواصل</Label>
              <Input value={sForm.office_address} onChange={(e) => setSForm((s) => ({ ...s, office_address: e.target.value }))} placeholder="مثال: دمشق - شارع بغداد - هاتف 011xxxxxxx" className="h-10" data-testid="settings-office-address" />
            </div>
            <div>
              <Label className="text-xs">رقم واتساب المكتب (يظهر كرمز QR على الإيصال)</Label>
              <Input value={sForm.office_whatsapp} onChange={(e) => setSForm((s) => ({ ...s, office_whatsapp: e.target.value }))} placeholder="مع رمز الدولة، مثال 9639xxxxxxxx" className="h-10 tabular-nums" dir="ltr" data-testid="settings-office-whatsapp" />
            </div>
            <div>
              <Label className="text-xs">شعار الشركة (يظهر أعلى الإيصال)</Label>
              <div className="flex items-center gap-3 mt-1">
                {sForm.logo
                  ? <img src={sForm.logo} alt="logo" className="h-12 w-auto max-w-[120px] object-contain rounded border border-border bg-white p-1" data-testid="settings-logo-preview" />
                  : <div className="h-12 w-20 rounded border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground">لا شعار</div>}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="text-xs" data-testid="settings-logo-input" />
                {sForm.logo && <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => setSForm((s) => ({ ...s, logo: "" }))} data-testid="settings-logo-remove">إزالة</Button>}
              </div>
            </div>
            <div>
              <Label className="text-xs">رسالة المرسل (تأكيد الاستلام)</Label>
              <textarea value={sForm.wa_sender_template} onChange={(e) => setSForm((s) => ({ ...s, wa_sender_template: e.target.value }))} rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="settings-sender-template" />
              <div className="mt-1 rounded-md bg-emerald-50 border border-emerald-100 p-2 text-[11px] text-emerald-900 whitespace-pre-wrap leading-relaxed" data-testid="settings-sender-preview">{previewMsg(sForm.wa_sender_template)}</div>
            </div>
            <div>
              <Label className="text-xs">رسالة المستفيد (جاهزة للاستلام)</Label>
              <textarea value={sForm.wa_receiver_template} onChange={(e) => setSForm((s) => ({ ...s, wa_receiver_template: e.target.value }))} rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="settings-receiver-template" />
              <div className="mt-1 rounded-md bg-emerald-50 border border-emerald-100 p-2 text-[11px] text-emerald-900 whitespace-pre-wrap leading-relaxed" data-testid="settings-receiver-preview">{previewMsg(sForm.wa_receiver_template)}</div>
            </div>
            <div className="text-[11px] text-muted-foreground bg-muted/50 rounded-md p-2 leading-relaxed">
              الحقول المتاحة: <code dir="ltr">{"{sender_name} {receiver_name} {code} {amount} {currency} {payout_amount} {payout_currency} {commission} {status} {destination} {agent} {receipt_link} {company_name} {office_address}"}</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSForm((s) => ({ ...s, wa_sender_template: DEFAULT_WA_SENDER, wa_receiver_template: DEFAULT_WA_RECEIVER }))} data-testid="settings-reset">استعادة الافتراضي</Button>
            <Button onClick={saveSettings} disabled={savingSet} data-testid="settings-save"><Save className="w-4 h-4 me-2" /> {savingSet ? "جارٍ الحفظ..." : "حفظ"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
